import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Search, ArrowUpDown, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showError } from '../components/Alerts';

interface CACIndex {
  id: number;
  period: string;
  general: number;
  materials: number;
  labourForce: number;
}

type SortCol = 'period' | 'general' | 'materials' | 'labourForce';

const ITEMS_PER_PAGE = 15;

const CACTab: React.FC = () => {
  const [data, setData] = useState<CACIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<SortCol>('period');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const fetchCACIndices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/cac/indices');
      setData(response.data);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCACIndices();
  }, [fetchCACIndices]);

  // Reset page when search changes
  useEffect(() => { setPage(1); }, [searchTerm, sortColumn, sortDirection]);

  const formatPeriod = (period: string) => {
    const [year, month] = period.split('-');
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
  };

  const formatNumber = (value: number) =>
    value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSort = (col: SortCol) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const displayData = (() => {
    let filtered = [...data];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        formatPeriod(item.period).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortColumn === 'period') cmp = new Date(a.period).getTime() - new Date(b.period).getTime();
      else if (sortColumn === 'general') cmp = a.general - b.general;
      else if (sortColumn === 'materials') cmp = a.materials - b.materials;
      else cmp = a.labourForce - b.labourForce;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return filtered;
  })();

  const totalPages = Math.ceil(displayData.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageItems = displayData.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const SortButton = ({ col, label }: { col: SortCol; label: string }) => (
    <button
      onClick={() => handleSort(col)}
      className="flex items-center gap-1.5 text-xs font-medium transition-colors text-muted-foreground hover:text-foreground group"
    >
      {label}
      <ArrowUpDown className={`w-3.5 h-3.5 transition-colors ${sortColumn === col ? 'text-primary' : 'group-hover:text-foreground'}`} />
    </button>
  );

  if (loading && data.length === 0) {
    return <LoadingScreen message="Cargando índices CAC" />;
  }

  return (
    <>
      {/* Toolbar */}
      <div className="px-6 py-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
          <RefreshCw className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Los índices se actualizan automáticamente a diario. Se muestran los últimos datos disponibles.</span>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por período..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        {displayData.length === 0 ? (
          <div className="py-14 text-center px-6">
            <TrendingUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground mb-1">No hay datos disponibles</p>
            <p className="text-xs text-muted-foreground">
              {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'Los índices se cargarán automáticamente en la próxima actualización diaria'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-6 py-3 text-left">
                  <SortButton col="period" label="Período" />
                </th>
                <th className="px-6 py-3 text-right">
                  <div className="flex justify-end">
                    <SortButton col="general" label="CAC General" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right">
                  <div className="flex justify-end">
                    <SortButton col="labourForce" label="Mano de Obra" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right">
                  <div className="flex justify-end">
                    <SortButton col="materials" label="Materiales" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pageItems.map(item => (
                <tr key={item.id} className="transition-colors hover:bg-muted/30">
                  <td className="px-6 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <TrendingUp className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{formatPeriod(item.period)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumber(item.general)}</span>
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumber(item.labourForce)}</span>
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumber(item.materials)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer con paginación */}
      {displayData.length > 0 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Mostrando{' '}
            <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
            <span className="font-semibold text-foreground">{displayData.length}</span>{' '}
            registro{displayData.length !== 1 ? 's' : ''}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={safePage === 1}
                className="rounded-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-foreground tabular-nums px-1">{safePage} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={safePage === totalPages}
                className="rounded-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CACTab;
